import { postForm, type FormFields } from '@/shared/api/client';
import { submitForm } from '@/shared/api/submit';
import { dateInputToUnix } from '@/shared/lib/dateInput';
import { formatUnixDate } from '@/shared/lib/datetime';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { normalizeOptions } from '@/shared/lib/normalizeOptions';
import type { ListResponse } from '@/shared/ui/list/types';
import type { SelectOption } from '@/shared/ui/selectTypes';

export interface MetrikaGoal {
  id: number;
  name: string;
  type: string;
  goals_id: number;
  local_type: string | number | null;
}

export interface MetrikaUtm {
  id: number;
  utm_type: string;
  name: string;
  source_type: string | number | null;
}

const FILTER_KEYS = ['periods', 'device', 'roles', 'source', 'partners', 'os', 'goals_types'] as const;

export type MetrikaFilterOptions = Record<'periods' | 'device' | 'roles' | 'sources' | 'partners' | 'os' | 'goalsTypes', SelectOption[]>;

export async function fetchMetrikaFilters(): Promise<MetrikaFilterOptions> {
  const res = await postForm<{ filter_list?: Partial<Record<(typeof FILTER_KEYS)[number], unknown[]>> }>(
    '/analytics/default/get-filters/',
    {},
  );
  const options = (key: (typeof FILTER_KEYS)[number]) =>
    normalizeOptions((res.filter_list?.[key] ?? []) as never[]);
  return {
    periods: options('periods'),
    device: options('device'),
    roles: options('roles'),
    sources: options('source'),
    partners: options('partners'),
    os: options('os'),
    goalsTypes: options('goals_types'),
  };
}

export async function fetchGoals() {
  const res = await postForm<ListResponse>('/analytics/goals/list/', {});
  return getListRows<MetrikaGoal & Record<string, unknown>>(res, ['goals']);
}

export const updateGoal = (goalsId: number, localType: string) =>
  submitForm('/analytics/goals/update/', { goals_id: goalsId, local_type: localType });

export async function fetchUtms() {
  const res = await postForm<ListResponse>('/analytics/utm/list/', {});
  return getListRows<MetrikaUtm & Record<string, unknown>>(res, ['utms']);
}

export const updateUtm = (utmId: number, sourceId: string) =>
  submitForm('/analytics/utm/update/', { utm_id: utmId, source_id: sourceId });

// ---- shared filters of the users / activity / traffic tabs ------------------

export interface MetrikaFilterValues {
  dateStart: string;
  dateEnd: string;
  period: string;
  device: string[];
  roles: string[];
  source: string[];
  partners: string[];
  os: string[];
  /** '1' / '0' / '' (any). */
  isApp: string;
  /** Backend field the search text is sent under (`user_id`, `phone`, ...). */
  searchField: string;
  search: string;
}

export const EMPTY_METRIKA_FILTERS: MetrikaFilterValues = {
  dateStart: '',
  dateEnd: '',
  period: '',
  device: [],
  roles: [],
  source: [],
  partners: [],
  os: [],
  isApp: '',
  searchField: '',
  search: '',
};

/** Request fields understood by every `/analytics/default/*` endpoint and by the analytics export. */
export const toFilterFields = (v: MetrikaFilterValues): FormFields => ({
  ...(v.searchField ? { [v.searchField]: v.search } : {}),
  date_start: dateInputToUnix(v.dateStart),
  date_end: dateInputToUnix(v.dateEnd),
  period_id: v.period,
  device_type: v.device,
  role: v.roles,
  source_id: v.source,
  partner_id: v.partners,
  os_id: v.os,
  have_app: v.isApp,
});

// ---- charts -----------------------------------------------------------------

export interface ChartPoint {
  date: string;
  data: number;
}

export const METRIKA_CHART_ENDPOINTS = {
  bids: '/analytics/default/bids/',
  appeals: '/analytics/default/requests-forms/',
  traffic: '/analytics/default/traffic/',
  partnersTraffic: '/analytics/default/partners-traffic/',
} as const;

export type MetrikaChartEndpoint = keyof typeof METRIKA_CHART_ENDPOINTS;

export async function fetchChart(endpoint: MetrikaChartEndpoint, filters: MetrikaFilterValues) {
  const res = await postForm<ListResponse>(METRIKA_CHART_ENDPOINTS[endpoint], toFilterFields(filters));
  return getListRows<{ date: number | string; data: number | string }>(res, ['data']).map(
    (point): ChartPoint => ({ date: formatUnixDate(point.date), data: Number(point.data) || 0 }),
  );
}

// ---- users ------------------------------------------------------------------

export interface MetrikaUser {
  id: number;
  user_id: number;
  user_name: string;
  phone: string | null;
  email: string;
  role: string;
  source: string;
  device: string;
  os: string;
  app: string;
  bids: number;
  visit_date: number;
  goals?: Array<{ id: number; name: string }> | null;
}

export async function fetchMetrikaUsers(filters: MetrikaFilterValues, page: number) {
  const res = await postForm<ListResponse>('/analytics/default/users-list/', {
    ...toFilterFields(filters),
    page,
  });
  return {
    users: getListRows<MetrikaUser & Record<string, unknown>>(res, ['data']),
    totalPages: getTotalPages(res),
  };
}
